import React, { useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/HelpOutline';
import OpenIcon from '@material-ui/icons/OpenInNew';
import {
  Link,
  sidebarConfig,
  SidebarItem,
} from '@backstage/core-components';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
// import {
//   aboutPageRouteRef,
//   simpleSupportPageRouteRef,
// } from '../../routes';
// import { useEpDevPAppContext } from '../../contexts';

const useStyles = makeStyles<BackstageTheme>((theme) => ({
  drawer: {
    minWidth: sidebarConfig.drawerWidthOpen,
    borderRadius: 0,
    background: theme.palette.background.paper,
    zIndex: 1000,
  },
  menu: {
    padding: theme.spacing(0),
    '&:focus': {
      outline: 'none',
    },
  },
  menuItem: {
    color: theme.palette.text.primary,
    justifyContent: 'space-between',
  },
  menuItemLabel: {
    padding: theme.spacing(0.25, 0),
  },
}));

/**
 * Properties for {@link EpDevPHelpSidebarItem}.
 * 
 * @alpha
 */
export type EpDevPSidebarProps = {
  /**
   * The sidebar item label. The default is "Help".
   */
  title?: string;
  /**
   * The base path for the {@link EpDevPRouter}.
   */
  basePath: string;
}

/**
 * A {@link SidebarItem} component which, when clicked, opens the "Help" menu.
 * 
 * @alpha
 */
export const EpDevPHelpSidebarItem = ({ title, basePath }: EpDevPSidebarProps) => {
  const { t } = useServiceRegistryTranslation();

  const classes = useStyles();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  // const [epDevPAppContextState] = useEpDevPAppContext();

  const handleSidebarItemOnClick = (event: React.MouseEvent) => {
    setAnchorElement(event.currentTarget as HTMLElement);
    setIsMenuOpen(true);
  }

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'tab') {
      event.preventDefault();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsMenuOpen(false);
      // restore focus to sidebar item
      if (anchorElement) { anchorElement.focus(); }
    }
  }

  // const supportLink = epDevPAppContextState.support.link;
  // const supportPageLocation = `${basePath}${simpleSupportPageRouteRef.path}`;
  // const aboutPageLocation = `${basePath}${aboutPageRouteRef.path}`;

  return (
    <>
      <SidebarItem
        aria-owns={isMenuOpen ? 'help-menu' : undefined}
        aria-haspopup="true"
        icon={HelpIcon}
        text={title ?? t('help.title')}
        onClick={handleSidebarItemOnClick}
      />
      <Popper
        id={isMenuOpen ? 'help-menu' : undefined}
        anchorEl={anchorElement}
        open={isMenuOpen}
        transition
        placement="right-end"
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className={classes.drawer} elevation={4}>
              <ClickAwayListener onClickAway={() => setIsMenuOpen(false)}>
                <MenuList
                  className={classes.menu}
                  autoFocus
                  variant="menu"
                  onKeyDown={handleMenuKeyDown}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <MenuItem component={Link} to={''} className={classes.menuItem}>
                    <Typography className={classes.menuItemLabel} variant="body1" noWrap>{t('help.contactSupport')}</Typography>
                  </MenuItem>
                  <Divider />
                  <MenuItem component={Link} to={''} className={classes.menuItem}>
                    <Typography className={classes.menuItemLabel} variant="body1" noWrap>{t('help.about')}</Typography>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
